import { ApolloClient } from 'apollo-client';
import type { ApolloLink } from 'apollo-link';
import { from, split } from 'apollo-link';
import { HttpLink } from 'apollo-link-http';
import { BatchHttpLink } from 'apollo-link-batch-http';

import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { cfetch, NoNetworkError, causedByAbortError } from '@confluence/network';
import { ClientNetworkStatus } from '@confluence/legacy-schema-bridge/entry-points/useNetworkStatusListenerCache.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { getQueryName } from '@confluence/query-preloader-tools/entry-points/getQueryName.ts';

import { isFastPreloadQuery } from './isFastPreloadQuery.ts';
import { networkStatusLink, writeNetworkStatus } from './links/NetworkStatusLink.ts';
import { externalShareLink } from './links/ExternalShareLink.ts';
import { addSuperAdminDirectiveLink } from './links/SuperAdminLink.ts';
import { queryOverridesLink } from './links/QueryOverridesLink.ts';
import { networkErrorRetryLink } from './links/NetworkErrorRetryLink.ts';
import { createOnErrorLink } from './links/OnErrorLink.ts';
import { statusCodeRetryLink } from './links/StatusCodeRetryLink.ts';
import { graphqlSyntheticErrorGenerator } from './links/DevOnlyGraphqlErrorGenerator.ts';
import { webSocketLink } from './links/WebSocketLink.ts';
import { SSRLink } from './links/SSRLink.ts';
import { SSRTestLink } from './links/SSRTestLink.ts';
import { fragmentMatcher } from './fragmentMatcher.ts';
import { dataIdFromObject } from './dataIdFromObject.ts';
import { cacheRedirects } from './cacheRedirects.ts';
import { isWS } from './graphqlAssertions.ts';
import { createWrappedFetch, AGG_SLOW_URI } from './createWrappedFetch.ts';
import { SSRDataLink } from './links/SSRServerLink.ts';
import { SSRClientLink } from './links/SSRClientLink.ts';
import { BroadcastConfigurableInMemoryCache } from './broadcastConfigurableInMemoryCache.ts';
import type { NormalizedCacheObject } from 'apollo-cache-inmemory';

// NOTE: URI should be cleaned up along with `cc-agg-migration-use-agg-endpoint`, once all skip queries are gone
const URI = '/cgraphql';
const MAX_CONCURRENCY_FOR_THROTTLED = 8;
const wrappedFetch = createWrappedFetch({ isForAggOnly: false });
const SSR_NAMESPACE = 'cgraphql';
const DEFAULT_MAX_BATCH_SIZE = 4;
const DEFAULT_BATCH_INTERVAL = 0;

export function resetApolloStats(): void {
	window.__APOLLO_STATS__ = {
		// Used in node_modules/apollo-client/bundle.esm.js
		broadcast: {},
		diffStore: {},
	};
}

const createCache = (): BroadcastConfigurableInMemoryCache => {
	return new BroadcastConfigurableInMemoryCache({
		fragmentMatcher,
		dataIdFromObject,
		cacheRedirects,
	});
};

/**
 * Override fetch function used in terminating (http batch) link, to throttle network requests.
 * Concurrent network requests are limited based on MAX_CONCURRENCY_FOR_THROTTLED constant.
 *
 * Realistically, this will be used when there are a large volume of queries are triggered together, upon page load. Therefore,
 * resolving requests in approximate order is sufficient.
 */
const requestPool: Promise<Response>[] = [];
const throttledFetch = (uri: string, options: RequestInit) => {
	if (requestPool.length >= MAX_CONCURRENCY_FOR_THROTTLED) {
		// pool is full, so queue up next request behind the first request in the pool
		const queuedFetchFn = async () => {
			await requestPool.shift();
			return wrappedFetch(uri, options);
		};

		const queuedFetch: Promise<Response> = queuedFetchFn();
		requestPool.push(queuedFetch);
		return queuedFetch;
	}

	// pool has available slot - add new request to pool and begin request immediately
	const nextFetch = wrappedFetch(uri, options);
	requestPool.push(nextFetch);
	return nextFetch;
};

const createLink = ({
	gatewayGraphqlSchemaLinkOverride,
}: {
	gatewayGraphqlSchemaLinkOverride?: ApolloLink;
}) => {
	const httpLinkOptions = {
		uri: URI,
		credentials: 'same-origin',
		fetch: wrappedFetch,
	};

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		const SSRLinks: ApolloLink[] = [];
		if (process.env.BIFROST_TEMPLATE_BUILD !== 'true') {
			SSRLinks.push(addSuperAdminDirectiveLink);
		}
		SSRLinks.push(SSRDataLink(SSR_NAMESPACE), SSRLink());

		if (process.env.CLOUD_ENV === 'staging' || process.env.CLOUD_ENV === 'branch') {
			SSRLinks.push(SSRTestLink());
		}
		return from(SSRLinks).concat(
			gatewayGraphqlSchemaLinkOverride ||
				split(
					({ getContext, operationName, variables }) =>
						getContext().single ||
						!isFastPreloadQuery(getQueryName(operationName, variables, true)),
					new HttpLink(httpLinkOptions),
					new BatchHttpLink({
						...httpLinkOptions,
						batchMax: DEFAULT_MAX_BATCH_SIZE,
						batchInterval: DEFAULT_BATCH_INTERVAL,
					}),
				),
		);
	} else {
		const httpLink = split(
			({ getContext, operationName, variables }) => {
				if (expVal('no_query_batching_on_client-side', 'isEnabled', false)) {
					// During an SPA transition (window.__SSR_RENDERED__ === false) keep batching the
					// fast-preload queries, even though client-side batching is otherwise disabled by
					// the no_query_batching_on_client-side experiment.
					if (
						window.__SSR_RENDERED__ === false &&
						isFastPreloadQuery(getQueryName(operationName, variables, true))
					) {
						return false;
					}
					return true;
				} else {
					return (
						getContext().single ||
						// In integration tests we deliberately block some queries in order to test the loading state
						// Randomly batching other queries with them will make UI fail to load
						// @ts-ignore TODO FIXME
						window.Cypress ||
						window.__CRITERION__ ||
						// @ts-ignore Property 'isPlaywright' does not exist on type 'Window & typeof globalThis'
						Boolean(window.isPlaywright) ||
						// we don't want query batching for contract tests
						// @ts-expect-error Property isContract doesn't exist on window
						Boolean(window.isContract) ||
						!StatsigConfigurations.isKillSwitchOn('confluence_spa_query_batching')
					);
				}
			},
			new HttpLink(httpLinkOptions),
			split(
				({ getContext }) => !!getContext().throttle,
				new HttpLink({ ...httpLinkOptions, fetch: throttledFetch }),
				new BatchHttpLink(httpLinkOptions),
			),
		);

		const processingLinks = [
			addSuperAdminDirectiveLink,
			SSRClientLink(SSR_NAMESPACE, apolloCache),
			networkStatusLink(),
			statusCodeRetryLink,
			networkErrorRetryLink,
			externalShareLink,
		];

		let ccGraphLink: ApolloLink;
		if (process.env.NODE_ENV === 'testing') {
			// SSR and test env don't support websocket
			ccGraphLink = from(processingLinks).concat(httpLink);
		} else {
			ccGraphLink = from(processingLinks).split(isWS, webSocketLink(), httpLink);
		}

		const links = [createOnErrorLink(), graphqlSyntheticErrorGenerator()];

		// prevents adding bundle size for prod customers
		if (
			process.env.CLOUD_ENV === 'hello' ||
			process.env.CLOUD_ENV === 'staging' ||
			process.env.CLOUD_ENV === 'branch' ||
			(process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'testing')
		) {
			links.unshift(queryOverridesLink());
		}

		return from(links).concat(gatewayGraphqlSchemaLinkOverride || ccGraphLink);
	}
};

export const apolloCache: BroadcastConfigurableInMemoryCache = createCache();

export const createClient = (
	options: {
		gatewayGraphqlSchemaLinkOverride?: ApolloLink;
		initializeNewCache?: boolean;
	} = {},
): ApolloClient<NormalizedCacheObject> => {
	// Creates window.__APOLLO_STATS__ call before creating the client
	resetApolloStats();
	const cache = options.initializeNewCache ? createCache() : apolloCache;
	if (typeof window['__APOLLO_STATE__'] === 'object') {
		cache.restore(window['__APOLLO_STATE__']);
	}
	const client = new ApolloClient({
		// Prevent cache and network query to refresh when they are already preloaded on server-side.
		// Refreshing data will cause component to fallback to loading state.
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		ssrMode: Boolean(process.env.REACT_SSR),
		link: createLink(options),
		cache,
	});

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR) {
		// Don't initialize the client cache for tests so we can mock it
		if (!options.gatewayGraphqlSchemaLinkOverride) {
			writeNetworkStatus(ClientNetworkStatus.ONLINE);
		}

		cfetch.subscribe((response, error) => {
			if (response) {
				// It's NetworkStatusLink's responsibility/concern to interpret
				// cc-graphql/agg errors (which may be carried in an OK HTTP response).
				const gqlEndpoint = fg('cc-agg-migration-use-agg-endpoint') ? AGG_SLOW_URI : URI;
				if (response.url?.indexOf(gqlEndpoint) === -1) {
					writeNetworkStatus(ClientNetworkStatus.ONLINE);
				}
			} else if (error instanceof NoNetworkError && !error.ignore && !causedByAbortError(error)) {
				writeNetworkStatus(ClientNetworkStatus.OFFLINE);
			}
		});
	}

	return client;
};
